/* eslint-disable @typescript-eslint/no-explicit-any */

import {useEffect, useRef} from 'react';

/**
 * Custom React hook - useComponentDidUpdate
 *
 * @template F - Type of the function to be executed on component updates.
 * @template A - Type of the array of dependencies for the useEffect hook.
 *
 * @param {F} func - The function to be executed on component updates.
 *                  It should be a function that takes no arguments and returns void or Function.
 *
 * @param {A} deps - An array of dependencies to be used with the useEffect hook.
 *                  The function 'func' will be executed only when these dependencies change.
 *
 * @description
 * This custom React hook allows you to run a specified function only after the initial
 * render of a component and on subsequent updates. It simulates the behavior of
 * componentDidUpdate lifecycle method in class components.
 *
 * The hook internally uses the useEffect hook and a useRef hook to keep track of
 * whether the component has mounted or not. On the initial render, it sets a flag
 * to indicate that the component has mounted. After that, on each update, it checks
 * the flag, and if it's not the initial render, it calls the provided function 'func'.
 *
 * @example
 * function ExampleComponent() {
 *   useComponentDidUpdate(() => {
 *     console.log('someProp updated!', props.someProp);
 *   }, [props.someProp]);
 * }
 *
 * @returns {void} Nothing is returned from this hook.
 */
export const useComponentDidUpdate = <
    F extends ((...args: Array<any>) => any) | (() => any),
    A extends Array<any>,
>(
    func: F,
    deps: A,
) => {
    const isComponentDidMount = useRef(true);
    useEffect(() => {
        if (isComponentDidMount.current) {
            isComponentDidMount.current = false;
            return undefined;
        } else {
            return func();
        }
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};
